
import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import * as _lodash from 'lodash';
import { AgmMap } from 'ng-agm-core-lib';

import { MapAction, MapConfig } from './tam-dashboard-map.model';
import { TamDashboardEvents } from '../../tam-dashboard.model';
import { Subscription } from 'rxjs';
import { businessConstants } from '../../../../constants/business.constants';
import { MapTripVehicleType } from '../map-trip/map-trip.model';
import { MapTileService, TripAutocompleteActionType } from '@tamalelibs/services/maptile.service';
import { TamDashboardTileData } from '../tam-dashboard-map-tile.config';
import { TamDashboardMapTileService } from '../tam-dashboard-map-tile.service';
import { filter } from 'rxjs/operators';
import { DirectionRendererService } from './direction-renderer.service';

@Component({
    selector: 'tam-dashboard-map',
    templateUrl: './tam-dashboard-map.component.html',
    styleUrls: ['./tam-dashboard-map.component.scss'],
    standalone: false
})
export class TamDashboardMapComponent implements OnInit, OnDestroy {
    @Input() config: MapConfig;
    @Input() id?: string;
    @Input() isShowEventOnMap = false;
    @ViewChild(AgmMap, { static: false }) agmMap: AgmMap;

    // Default center to avoid 0,0 location (middle of Atlantic Ocean)
    // Using San Francisco as default (can be changed to your preferred location)
    centerLatitude: number = 37.7749;
    centerlongitude: number = -122.4194;
    directionArray: Array<string>;
    destination = {};
    firstItem: TamDashboardTileData;
    marker: TamDashboardTileData;
    origin = {};
    waypoints: any = [];
    travelMode = MapTripVehicleType.driving;
    directionLoadDone = false;
    isMapTripShow = false;
    zoomIndex = 14;
    isFitBounds = true;
    isPrintMode = false;
    infoWindowOpened: any = null;
    infoWindowSingleOpened: any = null;
    previous_info_window: any = null;
    previous_info_window_single: any = null;
    myStyles = [
        {
            featureType: 'poi.business',
            elementType: 'labels',
            stylers: [
                { visibility: 'off' }
            ]
        },
        {
            featureType: 'poi.government',
            elementType: 'labels',
            stylers: [
                { visibility: 'off' }
            ]
        }

    ];

    private _subscribed = false;
    private _destroySubscriptions: Array<Subscription> = [];
    private _directionRenderer: DirectionRendererService;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _mapTileService: MapTileService,
        private _tamDashboardMapTileService: TamDashboardMapTileService,
        directionRenderer: DirectionRendererService
    ) {
        this._directionRenderer = directionRenderer;
    }

    ngOnInit() {
        this._initSubscriptions();

        if (this.isShowEventOnMap) {
            const action = {
                type: TamDashboardEvents.REFRESH_MAP_TILE,
                payload: this.config,
                isPrint: false
            };
            this._refreshMapTile(action);
        }
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._subscribed = false;
        if (this._directionRenderer) {
            this._directionRenderer.destroy();
        }
    }

    close_window() {
        if (this.previous_info_window) {
            this.previous_info_window.close();
        }
    }

    close_window_single() {
        if (this.previous_info_window_single) {
            this.previous_info_window_single.close();
        }
    }

    /**
     * support close POI
     *
     * @param {*} infoWindow
     * @memberof TamDashboardMapComponent
     */
    select_marker(data, infoWindow) {
        this._setIcons(data);
        if (this.config && this.config.markerArray && this.config.markerArray.length > 1) {
            this.previous_info_window_single = null;
            if (this.previous_info_window) {
                this.previous_info_window.close();
            }
            this.previous_info_window = infoWindow;
        } else {
            this.previous_info_window = null;
        }
    }

    select_marker_single(data, infoWindow) {
        this._setIcons(data);
        if (this.config && this.config.markerArray && this.config.markerArray.length === 1) {
            this.previous_info_window = null;
            if (this.previous_info_window_single) {
                this.previous_info_window_single.close();
            }
            this.previous_info_window_single = infoWindow;
        } else {
            this.previous_info_window_single = null;
        }
    }

    trackByFn(index, item) {
        return item.id;
    }

    onResponse(e: Event) {
        if (e && e['status'] === 'ZERO_RESULTS') {
            this._mapTileService.tripAutoCompleteActionSubject$.next({ type: TripAutocompleteActionType.ZERORESULTS });
        } else {
            this._mapTileService.tripAutoCompleteActionSubject$.next({ type: TripAutocompleteActionType.GETTRIP, payload: e });
        }
    }

    /**
     * Initialize direction renderer when map is ready
     */
    onMapReady(map: google.maps.Map) {
        if (map && this._directionRenderer) {
            this._directionRenderer.initialize(map);

            // If there's a pending direction to render, do it now
            if (this.isMapTripShow && this.origin && this.destination) {
                setTimeout(() => this._renderDirections(), 100);
            }
        }
    }

    /**
     * Render directions on the map
     */
    private _renderDirections() {
        if (!this._directionRenderer) {
            console.warn('Direction renderer not initialized yet');
            return;
        }

        if (!this.isMapTripShow || !this.origin || !this.destination) {
            this._directionRenderer.clearRoute();
            return;
        }

        this._directionRenderer.renderRoute(
            this.origin as string,
            this.destination as string,
            this.waypoints,
            this.travelMode
        ).then((result: google.maps.DirectionsResult) => {
            // Successfully rendered - trigger the onResponse callback
            this.onResponse(result as any);
        }).catch((error) => {
            // Handle errors (e.g., ZERO_RESULTS)
            console.warn('Direction rendering failed:', error);
            this.onResponse({ status: error.status } as any);
        });
    }

    private _initSubscriptions() {
        if (!this._subscribed) {
            this._subscribed = true;
            this._destroySubscriptions.push(
                this.config.actionSubject$.subscribe(action => this._refreshMapTile(action)),
                this._tamDashboardMapTileService.feedbackSubject$
                    .pipe(filter(res => res.id === this.id))
                    .subscribe(res => {
                        if (res.type === MapAction.OPEN_TRIP) {
                            this.config.markerArray.forEach((item: TamDashboardTileData) => {
                                // when trip is show .info icon change to 'addstop'
                                item.isAddStop = true;
                            });
                        }
                        if (res.type === MapAction.OPEN_TRIP || res.type === MapAction.ADD_STOP || res.type === MapAction.OPEN_NEARBY) {
                            if (this.previous_info_window !== null) {
                                this.previous_info_window.close();
                                this.previous_info_window = null;
                            }
                            if (this.previous_info_window_single !== null) {
                                this.previous_info_window_single.close();
                                this.previous_info_window = null;
                            }
                        }
                    })
            );
        }
    }

    private _refreshMapTile(action) {
        if (action.type === TamDashboardEvents.REFRESH_MAP_TILE) {
            // handle previous_info_window /previous_info_window_single not empty caused console issue.
            this.previous_info_window = null;
            this.previous_info_window_single = null;

            if (action.payload.markerArray && action.payload.markerArray.length <= 1) {
                setTimeout(() => {
                    this.zoomIndex = 14;
                });
                if (action.payload.markerArray.length === 1) {
                    this._center(action.payload.markerArray[0]);
                }
            }
            this.config.markerArray = action.payload.markerArray;
            this.isPrintMode = action.isPrint;
            // remove duplicate entity. need to keep the events which have the same linked entity address.
            this.config.markerArray = _lodash.uniqWith(this.config.markerArray, (marker1, marker2) => marker1.entityId === marker2.entityId && marker1.entityName === marker2.entityName && marker1.entityType === marker2.entityType);
            if (this.config.markerArray.length > 1) {
                this._setMarker();
            }
            if (this.config.markerArray.length === 1) {
                const a = _lodash.cloneDeep(this.config.markerArray);
                this.config.markerArray = a;
            }
            this.config.markerArray.forEach(item => {
                if (item.entityType === businessConstants.addressType.contact) {
                    item.iconOptions.url = businessConstants.iconAddress.yellow;
                } else if (item.entityType === businessConstants.addressType.entity) {
                    item.iconOptions.url = businessConstants.iconAddress.blue;
                } else if (item.entityType === businessConstants.addressType.duplicate) {
                    item.iconOptions.url = businessConstants.iconAddress.gray;
                } else if (item.entityType === businessConstants.addressType.google) {
                    item.iconOptions.url = businessConstants.iconAddress.red;
                } else if (item.entityType === businessConstants.addressType.event) {
                    item.iconOptions.url = businessConstants.iconAddress.wathet;
                }
                if (item.entityName) {
                    item.labelOptions.text = item.entityName.length < 18 ? item.entityName : item.entityName.substring(0, 18) + '...';
                }
            });
            // clear trip map search info
            this.isMapTripShow = false;
            this.origin = '';
            this.destination = '';
            this.waypoints = [];
            if (this._directionRenderer) {
                this._directionRenderer.clearRoute();
            }
        } else if (action.type === TamDashboardEvents.REFRESH_MAP_TRIP) {
            // get direciton info from trip pannel
            if (action.payload && action.payload.directionArray && action.payload.directionArray.length > 0) {
                this.config.markerArray = [];
                this.directionArray = _lodash.cloneDeep(action.payload.directionArray);
                if (action.payload.vehicleType) {
                    this.travelMode = action.payload.vehicleType;
                }

                this.waypoints = []; // clear waypoints
                this.origin = this.directionArray[0];
                this.destination = this.directionArray[this.directionArray.length - 1];
                // get waypoints
                this.directionArray.shift();
                this.directionArray.pop();
                this.directionArray.forEach(direction => {
                    this.waypoints.push({
                        location: direction
                    });
                });
                // show directions
                this.directionLoadDone = true;
                this.isMapTripShow = true;

                // Render the directions on the map
                setTimeout(() => this._renderDirections(), 100);
            }
        }
        this._updateChangesToUI();
    }


    /**
     * change icon and label styles
     * @param data
     */
    private _setIcons(data) {
        this.config.markerArray.forEach(item => {
            let icon = '';
            if (data === item) {
                // icon
                if (item.entityType === businessConstants.addressType.contact) {
                    icon = businessConstants.iconAddress.yellowSelected;
                } else if (item.entityType === businessConstants.addressType.entity) {
                    icon = businessConstants.iconAddress.blueSelected;
                } else if (item.entityType === businessConstants.addressType.google) {
                    icon = businessConstants.iconAddress.redSelected;
                } else if (item.entityType === businessConstants.addressType.duplicate) {
                    icon = businessConstants.iconAddress.graySelected;
                } else if (item.entityType === businessConstants.addressType.event) {
                    icon = businessConstants.iconAddress.wathetSelected;
                }
            } else {
                if (item.entityType === businessConstants.addressType.contact) {
                    icon = businessConstants.iconAddress.yellow;
                } else if (item.entityType === businessConstants.addressType.entity) {
                    icon = businessConstants.iconAddress.blue;
                } else if (item.entityType === businessConstants.addressType.google) {
                    icon = businessConstants.iconAddress.red;
                } else if (item.entityType === businessConstants.addressType.duplicate) {
                    icon = businessConstants.iconAddress.gray;
                } else if (item.entityType === businessConstants.addressType.event) {
                    icon = businessConstants.iconAddress.wathet;
                }
            }
            item.iconOptions = {
                'labelOrigin': businessConstants.lableLarge,
                'url': icon,
            };
        });
    }

    /**
     *
     * @private set same latitue&longitude to a group.
     * @memberof TamDashboardMapComponent
     */
    private _setMarker() {
        this.firstItem = _lodash.cloneDeep(this.config.markerArray[0]);
        const dataInfo = {};
        this.config.markerArray.forEach((item, index) => {
            const latAndLong = item.latitude + '&' + item.longitude;
            if (!dataInfo[latAndLong]) {
                dataInfo[latAndLong] = {
                    item,
                    child: [],
                    duplicate: {}
                };
            }
            dataInfo[latAndLong].child.push(item);
        });
        Object.values(dataInfo).forEach((element: any) => {
            const child = element.child;
            const duplicate = element.duplicate;
            child.forEach(item => {
                const address = item.address;
                if (!duplicate[address]) {
                    duplicate[address] = {
                        address,
                        list: []
                    };
                }
                duplicate[address].list.push(item);
            });
            element.duplicate = Object.values(duplicate);
        });
        const newData = Object.values(dataInfo);
        this.config.markerArray = [];
        newData.forEach((res: any) => {
            const data: TamDashboardTileData = res.item;
            data['originalName'] = _lodash.cloneDeep(data.entityName);
            if (res.child.length > 1) {
                data.duplicateAddress = _lodash.cloneDeep(res.duplicate);
                data.entityType = businessConstants.addressType.duplicate;
                data.entityName = businessConstants.mapString.multipleResult;
            }
            this.config.markerArray.push(data);
        });
    }

    private _center(marker) {
        if (marker) {
            this.centerLatitude = parseFloat(marker.latitude);
            this.centerlongitude = parseFloat(marker.longitude);
        }
    }

    private _updateChangesToUI() {
        if (!this._changeDetectorRef[businessConstants.common.changeDetectorDestroyedIdentifier]) {
            this._changeDetectorRef.detectChanges();
        }
    }

}
